// The profile card of a space or channel (18-channel-settings.md, General → Profile) and of the circle
// (27-admin.md, Circle → General): the name and the picture at the right, "Edit" turning it into a
// form with Upload and Remove, the name, any further fields, Save and Cancel.
import { useRef, useState, type ReactNode } from 'react';
import { Avatar } from '../blocks/Bits';
import { Button } from '../blocks/Button';
import { Field } from '../blocks/Field';
import { describe } from '../core/errors';
import { t } from '../i18n';
import { Card, Section } from './frame';

export type Picture = { file?: File; remove?: boolean };

interface Props {
  id: string;
  name: string;
  text?: ReactNode;
  src?: string;
  may: { edit: boolean; picture: boolean; name: boolean };
  /** More fields under the name (a channel's topic), and putting them back on Cancel. */
  fields?: ReactNode;
  onCancel?: () => void;
  onSave: (name: string, picture: Picture) => Promise<void>;
  words?: (error: unknown) => string;
  /** Initials without a picture: one for rooms, two for the circle (07's rule). */
  letters?: number;
}

export function ProfileCard({ id, name, text, src, may, fields, onCancel, onSave, words = (e) => describe(e, 'save').text, letters = 1 }: Props) {
  const [editing, setEditing] = useState(false);
  const [typed, setTyped] = useState(name);
  const [picture, setPicture] = useState<Picture>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const file = useRef<HTMLInputElement>(null);
  const save = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await onSave(typed.trim(), picture);
      setEditing(false);
      setPicture({});
    } catch (e) {
      setError(words(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section title={t('settings.profile')}>
      {!editing ? (
        <Card
          title={name || t('lobby.unknown')}
          text={text}
          control={
            <>
              <Avatar id={id} name={name} src={src} size={48} round={false} letters={letters} />
              {may.edit && (
                <Button small onClick={() => (setTyped(name), setEditing(true))}>
                  {t('chat.edit')}
                </Button>
              )}
            </>
          }
        />
      ) : (
        <Card title={t('settings.picture')} error={error}>
          <div className="settings-picture">
            <Avatar id={id} name={typed} src={picture.file ? URL.createObjectURL(picture.file) : picture.remove ? undefined : src} size={48} round={false} letters={letters} />
            {may.picture && (
              <>
                <Button small onClick={() => file.current?.click()}>
                  {t('settings.upload')}
                </Button>
                {picture.remove ? (
                  <Button small onClick={() => setPicture({})}>
                    {t('settings.reset')}
                  </Button>
                ) : (
                  (src || picture.file) && (
                    <Button small onClick={() => setPicture({ remove: true })}>
                      {t('settings.remove')}
                    </Button>
                  )
                )}
                <input ref={file} type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && setPicture({ file: e.target.files[0] })} />
              </>
            )}
          </div>
          <Field label={t('create.name')} value={typed} readOnly={!may.name} onChange={(e) => setTyped(e.target.value)} />
          {fields}
          <div className="settings-buttons">
            <Button small variant="filled" busy={busy} onClick={() => void save()}>
              {t('box.save')}
            </Button>
            <Button small variant="text" onClick={() => (setEditing(false), setPicture({}), onCancel?.())}>
              {t('common.cancel')}
            </Button>
          </div>
        </Card>
      )}
    </Section>
  );
}
